import { isSafari } from 'base/clipboard';
import React from 'react';

export function useKeyboardShortcuts(
  enable: boolean,
  handlersProp: Record<ShortcutDescription, (() => void) | undefined>,
) {
  const handlers = React.useRef(handlersProp);
  handlers.current = handlersProp;

  React.useEffect(() => {
    if (!enable) return;

    const onKey = (e: KeyboardEvent) => {
      if (isInputElement(e.target)) return;

      const key = shortcut(e.key, {
        cmd: e.ctrlKey || e.metaKey,
        shift: e.shiftKey,
        alt: e.altKey,
      });
      const handler = handlers.current[key];
      if (!handler) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      handler();
    };

    // In Safari, we need to use keyup to handle Cmd+Z, Cmd+C and other common shortcuts.
    // However, using keyup breaks Chrome on Mac (not chrome on linux; make sure to test on both).
    const eventName = isSafari() ? ('keyup' as const) : ('keydown' as const);
    document.addEventListener(eventName, onKey);
    return () => {
      document.removeEventListener(eventName, onKey);
    };
  }, [enable]);
}

type ShortcutDescription = `${boolean}-${boolean}-${boolean}-${string}`;
export function shortcut(
  key: string,
  { cmd = false, shift = false, alt = false } = {},
): ShortcutDescription {
  return `${cmd}-${shift}-${alt}-${key.toLowerCase()}`;
}

/** If the element is a text input */
export function isInputElement(e: EventTarget | null) {
  return (
    e instanceof Element &&
    e.closest('input, textarea, [contenteditable]') != null
  );
}
